// Google Drive API configuration
export const GOOGLE_CLIENT_ID = '654881529144-h1t5h3j071bmvbsk4qkh45rc2gqudcb7.apps.googleusercontent.com';
// API Key is required for Picker API - get from Google Cloud Console > APIs & Services > Credentials
export const GOOGLE_API_KEY = 'AIzaSyD_WB2-qPrQXs2kHkIulf3gb7j0VttyJRs';
// drive.file = access files created by this app
// drive.readonly = read-only access to all files (needed to import existing files)
// gmail.send = send emails via Gmail
export const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/gmail.send';

// Picker loaded flag
let pickerApiLoaded = false;

// Google Drive folder name for LTSS Analytics
export const LTSS_FOLDER_NAME = 'LTSS Analytics Reports';

interface GoogleAuthResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

let tokenClient: any = null;
let accessToken: string | null = null;

// Initialize Google Identity Services
export const initGoogleAuth = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window not available'));
      return;
    }

    // Check if script already loaded
    if ((window as any).google?.accounts?.oauth2) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
};

// Request access token
export const requestAccessToken = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (accessToken) {
      resolve(accessToken);
      return;
    }

    if (!(window as any).google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services not loaded'));
      return;
    }

    tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES,
      callback: (response: GoogleAuthResponse) => {
        if (response.access_token) {
          accessToken = response.access_token;
          resolve(response.access_token);
        } else {
          reject(new Error('Failed to get access token'));
        }
      },
      error_callback: (error: any) => {
        reject(new Error(error.message || 'OAuth error'));
      },
    });

    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
};

// Check if user is connected to Google Drive
export const isGoogleConnected = (): boolean => {
  return accessToken !== null;
};

// Load Google Picker API
export const loadPickerApi = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (pickerApiLoaded) {
      resolve();
      return;
    }

    // Load the Google API client library
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      (window as any).gapi.load('picker', {
        callback: () => {
          pickerApiLoaded = true;
          resolve();
        },
        onerror: () => reject(new Error('Failed to load Picker API')),
      });
    };
    script.onerror = () => reject(new Error('Failed to load Google API'));
    document.head.appendChild(script);
  });
};

// Open Google Drive Picker - native file browser
export const openGooglePicker = (
  onSelect: (file: { id: string; name: string; mimeType: string }) => void,
  onCancel?: () => void
): Promise<void> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Ensure auth and picker are loaded
      await initGoogleAuth();
      const token = await requestAccessToken();
      await loadPickerApi();

      const google = (window as any).google;
      
      // Create picker view for spreadsheets, CSVs, and Google Sheets
      const docsView = new google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes([
          'application/vnd.ms-excel',                                           // .xls
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',  // .xlsx
          'text/csv',                                                            // .csv
          'application/csv',                                                     // .csv alt
          'application/vnd.google-apps.spreadsheet',                            // Google Sheets
        ].join(','));
      
      // Also show recently used files
      const recentView = new google.picker.DocsView(google.picker.ViewId.RECENTLY_PICKED);

      const picker = new google.picker.PickerBuilder()
        .setAppId(GOOGLE_CLIENT_ID.split('-')[0]) // Extract app ID from client ID
        .setOAuthToken(token)
        .setDeveloperKey(GOOGLE_API_KEY)
        .addView(docsView)
        .addView(recentView)
        .setTitle('Select an Excel or CSV file')
        .setCallback((data: any) => {
          if (data.action === google.picker.Action.PICKED) {
            const file = data.docs[0];
            onSelect({
              id: file.id,
              name: file.name,
              mimeType: file.mimeType,
            });
            resolve();
          } else if (data.action === google.picker.Action.CANCEL) {
            onCancel?.();
            resolve();
          }
        })
        .build();

      picker.setVisible(true);
    } catch (err) {
      reject(err);
    }
  });
};

// Disconnect from Google Drive
export const disconnectGoogle = (): void => {
  if (accessToken && (window as any).google?.accounts?.oauth2) {
    (window as any).google.accounts.oauth2.revoke(accessToken);
  }
  accessToken = null;
};

// Find or create LTSS Analytics folder
const getOrCreateFolder = async (token: string): Promise<string> => {
  // Search for existing folder
  const searchResponse = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=name='${LTSS_FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  const searchData = await searchResponse.json();

  if (searchData.files && searchData.files.length > 0) {
    return searchData.files[0].id;
  }

  // Create new folder
  const createResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: LTSS_FOLDER_NAME,
      mimeType: 'application/vnd.google-apps.folder',
    }),
  });

  const createData = await createResponse.json();
  return createData.id;
};

// Upload file to Google Drive
export const uploadToGoogleDrive = async (
  file: Blob,
  fileName: string,
  mimeType: string = 'application/pdf'
): Promise<{ id: string; name: string; webViewLink: string }> => {
  const token = await requestAccessToken();
  const folderId = await getOrCreateFolder(token);

  // Create file metadata
  const metadata = {
    name: fileName,
    mimeType: mimeType,
    parents: [folderId],
  };

  // Create multipart request
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append('file', file);

  const response = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    }
  );

  if (!response.ok) {
    throw new Error('Failed to upload file to Google Drive');
  }

  return response.json();
};

// List files from LTSS Analytics folder (for saved reports)
export const listFilesFromDrive = async (): Promise<
  Array<{ id: string; name: string; modifiedTime: string; webViewLink: string }>
> => {
  const token = await requestAccessToken();
  const folderId = await getOrCreateFolder(token);

  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents and trashed=false&fields=files(id,name,modifiedTime,webViewLink)&orderBy=modifiedTime desc`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to list files from Google Drive');
  }

  const data = await response.json();
  return data.files || [];
};

// List Excel/CSV files from entire Google Drive (for importing reports)
export const listImportableFiles = async (): Promise<
  Array<{ id: string; name: string; modifiedTime: string; mimeType: string }>
> => {
  const token = await requestAccessToken();

  // Search for Excel and CSV files
  const mimeTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/csv',
  ];
  
  const mimeQuery = mimeTypes.map(m => `mimeType='${m}'`).join(' or ');
  
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=(${mimeQuery}) and trashed=false&fields=files(id,name,modifiedTime,mimeType)&orderBy=modifiedTime desc&pageSize=50`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to list files from Google Drive');
  }

  const data = await response.json();
  return data.files || [];
};

// Download file from Google Drive (handles Google Sheets by exporting as CSV)
export const downloadFromDrive = async (fileId: string, mimeType?: string): Promise<Blob> => {
  const token = await requestAccessToken();

  // Google Sheets need to be exported, not downloaded directly
  const isGoogleSheet = mimeType === 'application/vnd.google-apps.spreadsheet';
  
  const url = isGoogleSheet
    ? `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=text/csv`
    : `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to download file from Google Drive');
  }

  return response.blob();
};

// Delete file from Google Drive
export const deleteFromDrive = async (fileId: string): Promise<void> => {
  const token = await requestAccessToken();

  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to delete file from Google Drive');
  }
};

// Send email via Gmail API
export const sendEmailViaGmail = async (
  to: string,
  subject: string,
  htmlBody: string
): Promise<void> => {
  const token = await requestAccessToken();

  // Create the email in RFC 2822 format
  const email = [
    `To: ${to}`,
    `Subject: ${subject}`,
    'MIME-Version: 1.0',
    'Content-Type: text/html; charset=utf-8',
    '',
    htmlBody,
  ].join('\r\n');

  // Base64 URL encode the email
  const encodedEmail = btoa(unescape(encodeURIComponent(email)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const response = await fetch(
    'https://www.googleapis.com/gmail/v1/users/me/messages/send',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ raw: encodedEmail }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to send email');
  }
};

// Send team invite email
export const sendInviteEmail = async (
  toEmail: string,
  inviterName: string,
  role: string,
  inviteLink: string
): Promise<void> => {
  const subject = `You're invited to join LTSS Analytics`;
  
  const htmlBody = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background: linear-gradient(135deg, #3b82f6, #8b5cf6); padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
        <h1 style="color: white; margin: 0; font-size: 24px;">LTSS Analytics</h1>
        <p style="color: rgba(255,255,255,0.8); margin: 10px 0 0 0;">Team Invitation</p>
      </div>
      
      <div style="background: #f8fafc; padding: 30px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px;">
        <p style="color: #334155; font-size: 16px; line-height: 1.6;">
          Hi there,
        </p>
        
        <p style="color: #334155; font-size: 16px; line-height: 1.6;">
          <strong>${inviterName}</strong> has invited you to join their team on LTSS Analytics as a <strong>${role}</strong>.
        </p>
        
        <p style="color: #334155; font-size: 16px; line-height: 1.6;">
          LTSS Analytics helps healthcare organizations manage and analyze their EVV (Electronic Visit Verification) services data.
        </p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${inviteLink}" style="display: inline-block; background: linear-gradient(135deg, #3b82f6, #8b5cf6); color: white; text-decoration: none; padding: 14px 32px; border-radius: 8px; font-weight: bold; font-size: 16px;">
            Accept Invitation
          </a>
        </div>
        
        <p style="color: #64748b; font-size: 14px; line-height: 1.6;">
          This invitation will expire in 7 days. If you didn't expect this invitation, you can safely ignore this email.
        </p>
        
        <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 20px 0;">
        
        <p style="color: #94a3b8; font-size: 12px; text-align: center;">
          LTSS Analytics by Venus Health Care Solutions<br>
          <a href="https://www.ltssanalytics.com" style="color: #3b82f6;">www.ltssanalytics.com</a>
        </p>
      </div>
    </div>
  `;

  await sendEmailViaGmail(toEmail, subject, htmlBody);
};
